import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import authService from "../../../utils/authService";
import {
  fetchSubjectsOfCourses,
  type CourseSubjectOption,
} from "../../../utils/courseUtils";
import {
  fetchQuestionCountsBySubtopics,
  fetchSubtopicsForTopic,
  fetchTopicsForSubject,
  type ContentSubtopic,
  type ContentTopic,
  type LevelCountMap,
} from "../../../utils/contentUtils";
import { BsClock } from "react-icons/bs";
import { GiStarShuriken } from "react-icons/gi";
import TestMultiSelect from "../test/TestMultiSelect";
import {
  computePaperTotalsFromLogic,
  defaultLevelCounts,
  emptyLogic,
  type QuestionSelectionLogic,
  type SubtopicQuestionRule,
} from "../test/QuestionSelectionLogicModal";

const LEVELS = ["level1", "level2", "level3"] as const;
const LEVEL_LABELS: Record<(typeof LEVELS)[number], string> = {
  level1: "Easy",
  level2: "Medium",
  level3: "Hard",
};

type CourseOption = {
  course_id: string;
  course_name: string;
};

type Availability = { mcq: LevelCountMap; coding: LevelCountMap };

/** Subtopics enriched with topic/subject ids (same as QuestionSelectionLogicModal). */
type EnrichedSubtopic = ContentSubtopic & {
  subject_id: string;
  topic_id: string;
};

const logicEquals = (a: QuestionSelectionLogic, b: QuestionSelectionLogic) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Master Data → Course Test Selection
 * Course + subject dropdowns; Final Test selection defaults saved on courses.test_selection_logic.
 */
const CourseTestSelection: React.FC = () => {
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseId, setCourseId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [subjects, setSubjects] = useState<CourseSubjectOption[]>([]);
  const [fetchingCourses, setFetchingCourses] = useState(false);
  const [fetchingSubjects, setFetchingSubjects] = useState(false);

  const [logic, setLogic] = useState<QuestionSelectionLogic>(emptyLogic());
  const [savedLogic, setSavedLogic] = useState<QuestionSelectionLogic | null>(
    null
  );
  const [topics, setTopics] = useState<ContentTopic[]>([]);
  const [subtopics, setSubtopics] = useState<EnrichedSubtopic[]>([]);
  const [availability, setAvailability] = useState<
    Record<string, Availability>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lmsRules, setLmsRules] = useState<{
    mcq?: { level: string; score: string | number; time: string | number }[];
    coding?: { level: string; score: string | number; time: string | number }[];
  } | null>(null);

  const selectedSubject = subjects.find((s) => s.subject_id === subjectId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setFetchingCourses(true);
        const response = await authService.get(
          "admin/get_all_course_tracks_and_subjects/"
        );
        if (!cancelled) {
          const rows = (response.data.courses || []) as CourseOption[];
          setCourses(
            rows.map((c) => ({
              course_id: c.course_id,
              course_name: c.course_name,
            }))
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Failed to load courses.");
      } finally {
        if (!cancelled) setFetchingCourses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.get("admin/fetch_rules/");
        if (!cancelled) setLmsRules(res.data || null);
      } catch {
        if (!cancelled) setLmsRules(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSubjectId("");
    setSubjects([]);
    if (!courseId) return;
    let cancelled = false;
    (async () => {
      try {
        setFetchingSubjects(true);
        const rows = await fetchSubjectsOfCourses([courseId]);
        if (!cancelled) setSubjects(rows);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setSubjects([]);
          setError("Failed to load subjects for this course.");
        }
      } finally {
        if (!cancelled) setFetchingSubjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const loadMeta = useCallback(
    async (subjectIds: string[], topicIds: string[]) => {
      const topicLists = await Promise.all(
        subjectIds.map(async (id) => {
          const rows = await fetchTopicsForSubject(id);
          return rows.map((t) => ({ ...t, subject_id: t.subject_id || id }));
        })
      );
      const allTopics = topicLists.flat();
      setTopics(allTopics);

      const validTopicIds = topicIds.filter((tid) =>
        allTopics.some((t) => t.topic_id === tid)
      );
      const subLists = await Promise.all(
        validTopicIds.map(async (tid) => {
          const topic = allTopics.find((t) => t.topic_id === tid);
          const rows = await fetchSubtopicsForTopic(tid);
          return rows.map((s) => ({
            ...s,
            topic_id: tid,
            subject_id: topic?.subject_id || subjectIds[0] || "",
          }));
        })
      );
      const allSubtopics = subLists.flat();
      setSubtopics(allSubtopics);
      return { allTopics, allSubtopics };
    },
    []
  );

  const rebuildRules = (
    next: QuestionSelectionLogic,
    allSubtopics: EnrichedSubtopic[],
    allTopics: ContentTopic[]
  ): QuestionSelectionLogic => {
    const rules: SubtopicQuestionRule[] = next.sub_topic_ids.map((sid) => {
      const existing = next.subtopic_rules.find((r) => r.sub_topic_id === sid);
      const meta = allSubtopics.find((s) => s.sub_topic_id === sid);
      const subject = subjects.find((s) => s.subject_id === meta?.subject_id);
      const topic = allTopics.find((t) => t.topic_id === meta?.topic_id);
      return {
        subject_id: meta?.subject_id || existing?.subject_id || subjectId,
        topic_id: meta?.topic_id || existing?.topic_id || "",
        sub_topic_id: sid,
        subject_name:
          subject?.subject_name ||
          selectedSubject?.subject_name ||
          existing?.subject_name,
        topic_name: topic?.topic_name || existing?.topic_name,
        sub_topic_name: meta?.sub_topic_name || existing?.sub_topic_name,
        level_counts: existing?.level_counts?.length
          ? existing.level_counts
          : defaultLevelCounts(),
      };
    });
    return { ...next, subject_ids: [subjectId], subtopic_rules: rules };
  };

  useEffect(() => {
    if (!courseId || !subjectId) {
      setLogic(emptyLogic());
      setSavedLogic(null);
      setTopics([]);
      setSubtopics([]);
      setAvailability({});
      setSuccess("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const res = await authService.get(
          `admin/courses/${courseId}/test-selection-logic/`,
          { params: { subject_id: subjectId } }
        );
        if (cancelled) return;
        const loaded: QuestionSelectionLogic = res.data?.logic || {
          ...emptyLogic(),
          subject_ids: [subjectId],
        };
        loaded.subject_ids = [subjectId];
        const { allTopics, allSubtopics } = await loadMeta(
          [subjectId],
          loaded.topic_ids || []
        );
        if (cancelled) return;
        const normalized = rebuildRules(loaded, allSubtopics, allTopics);
        setLogic(normalized);
        setSavedLogic(res.data?.logic ? normalized : null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Failed to load selection logic.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, subjectId, loadMeta]);

  useEffect(() => {
    if (!logic.sub_topic_ids.length) {
      setAvailability({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchQuestionCountsBySubtopics(logic.sub_topic_ids);
        if (!cancelled) setAvailability(next);
      } catch {
        if (!cancelled) {
          const fallback: Record<string, Availability> = {};
          for (const sid of logic.sub_topic_ids) {
            fallback[sid] = {
              mcq: { level1: 0, level2: 0, level3: 0 },
              coding: { level1: 0, level2: 0, level3: 0 },
            };
          }
          setAvailability(fallback);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logic.sub_topic_ids]);

  const topicOptions = useMemo(
    () =>
      topics.map((t) => ({
        value: t.topic_id,
        label: t.topic_name,
      })),
    [topics]
  );

  const subtopicOptions = useMemo(
    () =>
      subtopics
        .filter((s) => logic.topic_ids.includes(s.topic_id))
        .map((s) => ({
          value: s.sub_topic_id,
          label: s.sub_topic_name,
        })),
    [subtopics, logic.topic_ids]
  );

  const hasChanges =
    !savedLogic || !logicEquals(logic, savedLogic);

  const totals = useMemo(() => {
    let mcq = 0;
    let coding = 0;
    let availMcq = 0;
    let availCoding = 0;
    logic.subtopic_rules.forEach((rule) => {
      const avail = availability[rule.sub_topic_id];
      rule.level_counts.forEach((lc) => {
        mcq += lc.mcq_count;
        coding += lc.coding_count;
        const level = lc.level as keyof LevelCountMap;
        availMcq += Number(avail?.mcq?.[level] || 0);
        availCoding += Number(avail?.coding?.[level] || 0);
      });
    });
    return { mcq, coding, availMcq, availCoding, total: mcq + coding };
  }, [logic.subtopic_rules, availability]);

  const paperTotals = useMemo(
    () => computePaperTotalsFromLogic(logic, lmsRules),
    [logic, lmsRules]
  );

  const handleTopicsChange = async (topicIds: string[]) => {
    const nextBase = {
      ...logic,
      subject_ids: [subjectId],
      topic_ids: topicIds,
      sub_topic_ids: [],
      subtopic_rules: [],
    };
    setLogic(nextBase);
    await loadMeta([subjectId], topicIds);
  };

  const handleSubtopicsChange = (subTopicIds: string[]) => {
    setLogic(
      rebuildRules({ ...logic, sub_topic_ids: subTopicIds }, subtopics, topics)
    );
  };

  const setCount = (
    subTopicId: string,
    level: string,
    field: "mcq_count" | "coding_count",
    value: number
  ) => {
    setLogic({
      ...logic,
      subtopic_rules: logic.subtopic_rules.map((rule) => {
        if (rule.sub_topic_id !== subTopicId) return rule;
        return {
          ...rule,
          level_counts: rule.level_counts.map((lc) => {
            if (lc.level !== level) return lc;
            const available = availability[subTopicId];
            const max =
              field === "mcq_count"
                ? Number(available?.mcq?.[level as keyof LevelCountMap] || 0)
                : Number(
                    available?.coding?.[level as keyof LevelCountMap] || 0
                  );
            return {
              ...lc,
              [field]: Math.max(
                0,
                Math.min(Number.isFinite(value) ? value : 0, max)
              ),
            };
          }),
        };
      }),
    });
  };

  const canSave =
    Boolean(courseId && subjectId) &&
    logic.topic_ids.length > 0 &&
    totals.total > 0 &&
    hasChanges;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = rebuildRules(logic, subtopics, topics);
      await authService.put(
        `admin/courses/${courseId}/test-selection-logic/`,
        { subject_id: subjectId, logic: payload }
      );
      setLogic(payload);
      setSavedLogic(payload);
      setSuccess("Final Test selection saved for this course + subject.");
    } catch (err) {
      console.error(err);
      setError("Failed to save selection logic.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: "calc(100vh - 90px)", overflowY: "auto" }}
    >
      {(fetchingCourses || loading) && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      <div className="container-fluid">
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="py-2"
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccess("")}
            className="py-2"
          >
            {success}
          </Alert>
        )}

        <div className="row mb-3 align-items-center gx-3">
          <div className="col-auto d-flex align-items-center">
            <label htmlFor="cts-course" className="me-2 mb-0">
              Select Course :
            </label>
            <Form.Select
              id="cts-course"
              size="sm"
              className="dropdown-scroll"
              style={{ width: "180px" }}
              value={courseId}
              disabled={fetchingCourses}
              onChange={(e) => setCourseId(e.target.value)}
            >
              <option value="">Select course</option>
              {courses.map((c) => (
                <option key={c.course_id} value={c.course_id}>
                  {c.course_name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-auto d-flex align-items-center">
            <label htmlFor="cts-subject" className="me-2 mb-0">
              Select Subject :
            </label>
            <Form.Select
              id="cts-subject"
              size="sm"
              className="dropdown-scroll"
              style={{ width: "180px" }}
              value={subjectId}
              disabled={
                !courseId ||
                fetchingCourses ||
                fetchingSubjects ||
                loading
              }
              onChange={(e) => setSubjectId(e.target.value)}
            >
              <option value="">Select Subject</option>
              {subjects.map((s) => (
                <option key={s.subject_id} value={s.subject_id}>
                  {s.subject_name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col d-flex justify-content-end align-items-center gap-3">
            {courseId && subjectId && (
              <div className="d-flex flex-wrap gap-3 small text-secondary">
                <span className="d-inline-flex align-items-center gap-1">
                  <BsClock />
                  {paperTotals.minutes}{" "}
                  {paperTotals.minutes === 1 ? "Minute" : "Minutes"}
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <GiStarShuriken />
                  {paperTotals.marks}{" "}
                  {paperTotals.marks === 1 ? "Mark" : "Marks"}
                </span>
              </div>
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={!canSave || saving || loading || fetchingCourses}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Selection"}
            </Button>
          </div>
        </div>

        {!courseId || !subjectId ? (
          <div className="text-muted text-center border rounded p-4">
            Select a course and subject to configure Final Test selection logic.
          </div>
        ) : (
          <>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="d-flex justify-content-end gap-2 mb-1">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() =>
                      handleTopicsChange(topicOptions.map((o) => o.value))
                    }
                    disabled={topicOptions.length === 0 || loading}
                  >
                    All
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none text-muted"
                    onClick={() => handleTopicsChange([])}
                    disabled={topicOptions.length === 0 || loading}
                  >
                    Clear
                  </Button>
                </div>
                <TestMultiSelect
                  label="Topics"
                  value={logic.topic_ids}
                  onChange={handleTopicsChange}
                  options={topicOptions}
                  placeholder="Select topic(s)"
                  disabled={loading}
                />
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-end gap-2 mb-1">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() =>
                      handleSubtopicsChange(
                        subtopicOptions.map((o) => o.value)
                      )
                    }
                    disabled={logic.topic_ids.length === 0 || loading}
                  >
                    All
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none text-muted"
                    onClick={() => handleSubtopicsChange([])}
                    disabled={logic.topic_ids.length === 0 || loading}
                  >
                    Clear
                  </Button>
                </div>
                <TestMultiSelect
                  label="Subtopics"
                  value={logic.sub_topic_ids}
                  onChange={handleSubtopicsChange}
                  options={subtopicOptions}
                  disabled={!logic.topic_ids.length || loading}
                  placeholder={
                    logic.topic_ids.length === 0
                      ? "Select topics first"
                      : "Select subtopic(s)"
                  }
                />
              </Col>
            </Row>

            {logic.subtopic_rules.length === 0 ? (
              <div className="text-muted small border rounded p-3">
                Select topics and subtopics to configure question counts per
                level.
              </div>
            ) : (
              <div className="table-responsive border rounded">
                <Table size="sm" bordered className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th rowSpan={2} className="align-middle">
                        Topic
                      </th>
                      <th rowSpan={2} className="align-middle">
                        Subtopic
                      </th>
                      <th rowSpan={2} className="align-middle">
                        Level
                      </th>
                      <th colSpan={2} className="text-center">
                        MCQ
                      </th>
                      <th colSpan={2} className="text-center">
                        Coding
                      </th>
                    </tr>
                    <tr>
                      <th className="text-center small">Selected</th>
                      <th className="text-center small">Available</th>
                      <th className="text-center small">Selected</th>
                      <th className="text-center small">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logic.subtopic_rules.map((rule) =>
                      LEVELS.map((level, levelIndex) => {
                        const lc =
                          rule.level_counts.find((x) => x.level === level) || {
                            level,
                            mcq_count: 0,
                            coding_count: 0,
                          };
                        const avail = availability[rule.sub_topic_id];
                        const mcqAvail = Number(avail?.mcq?.[level] ?? 0);
                        const codingAvail = Number(
                          avail?.coding?.[level] ?? 0
                        );
                        return (
                          <tr key={`${rule.sub_topic_id}-${level}`}>
                            {levelIndex === 0 && (
                              <>
                                <td
                                  rowSpan={LEVELS.length}
                                  className="small"
                                >
                                  {rule.topic_name}
                                </td>
                                <td
                                  rowSpan={LEVELS.length}
                                  className="fw-semibold small"
                                >
                                  {rule.sub_topic_name}
                                </td>
                              </>
                            )}
                            <td className="small text-nowrap">
                              <Badge bg="primary" className="me-1">
                                L{level.replace("level", "")}
                              </Badge>
                              <span className="text-muted">
                                {LEVEL_LABELS[level]}
                              </span>
                            </td>
                            <td className="p-1 text-center">
                              <Form.Control
                                type="number"
                                size="sm"
                                min={0}
                                max={mcqAvail}
                                value={lc.mcq_count}
                                className="text-center mx-auto"
                                style={{ width: "4.5rem" }}
                                disabled={loading}
                                onChange={(e) =>
                                  setCount(
                                    rule.sub_topic_id,
                                    level,
                                    "mcq_count",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            </td>
                            <td className="text-center small text-muted">
                              {mcqAvail}
                            </td>
                            <td className="p-1 text-center">
                              <Form.Control
                                type="number"
                                size="sm"
                                min={0}
                                max={codingAvail}
                                value={lc.coding_count}
                                className="text-center mx-auto"
                                style={{ width: "4.5rem" }}
                                disabled={loading}
                                onChange={(e) =>
                                  setCount(
                                    rule.sub_topic_id,
                                    level,
                                    "coding_count",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            </td>
                            <td className="text-center small text-muted">
                              {codingAvail}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={3} className="fw-semibold small">
                        Total to assign
                      </td>
                      <td className="text-center fw-semibold">{totals.mcq}</td>
                      <td className="text-center text-muted small">
                        {totals.availMcq}
                      </td>
                      <td className="text-center fw-semibold">
                        {totals.coding}
                      </td>
                      <td className="text-center text-muted small">
                        {totals.availCoding}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CourseTestSelection;
